import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BackendService } from '../../../services/backend';
import { ClientConfigService } from '../../../services/client-config.service';
import { DateTime } from 'luxon';

@Component({
	selector: 'app-reports',
	templateUrl: './reports.component.html',
	styleUrls: ['./reports.component.scss'],
	imports: [CommonModule, FormsModule, NzDatePickerModule, NzButtonModule, NzPageHeaderModule]
})
export class ReportsComponent {
	backend = inject(BackendService);
	message = inject(NzMessageService);
	clientConfig = inject(ClientConfigService);

	fromTime: Date;
	tillTime: Date;

	get maxReportPeriodDays(): number {
		return this.clientConfig.config().angular.maxReportPeriodDays;
	}

	constructor() {
		const now = DateTime.now();
		this.fromTime = now.minus({ days: 1 }).startOf('day').toJSDate();
		this.tillTime = now.startOf('day').toJSDate();
	}

	generateReport() {
		if (!this.fromTime || !this.tillTime) {
			this.message.warning('Please select both From and Till times');
			return;
		}

		const from = DateTime.fromJSDate(this.fromTime);
		const till = DateTime.fromJSDate(this.tillTime);

		if (till < from) {
			this.message.warning('"Till" time must be after "From" time');
			return;
		}

		const maxDays = this.clientConfig.config().angular.maxReportPeriodDays;
		const diffDays = till.diff(from, 'days').days;
		if (diffDays > maxDays) {
			this.message.warning(`Action is limited to max period size of ${maxDays} days`);
			return;
		}

		// Naive local wall-clock strings (no offset) + the zone they were picked in. The server interprets
		// from/till in that zone, converts to UTC for the query, and renders the CSV's datetime columns back
		// in it. Sending an offset on the strings instead would get double-converted — don't.
		this.backend.downloadDetailedCsvReport(
			from.toISO({ includeOffset: false })!,
			till.toISO({ includeOffset: false })!,
			from.zoneName!
		).subscribe({
			next: (blob) => {
				const objectUrl = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = objectUrl;
				a.download = `report_${from.toFormat('yyyyMMdd')}_${till.toFormat('yyyyMMdd')}.csv`;
				a.click();
				URL.revokeObjectURL(objectUrl);
			},
			error: () => this.message.error('Failed to generate report'),
		});
	}
}
